// Reglas compartidas por las tarjetas de los tres tableros (estados, fases y
// etapas). Vivían duplicadas en cada página.

// Estatus fuera del funnel: no se pueden arrastrar, avanzar ni gestionar; la
// interacción queda en solo consulta. ÚNICA fuente: antes la lista estaba
// copiada a mano en una docena de componentes. «Perdido» (task_2, negocio
// perdido) es final, como Descartado.
export const CLOSED_STATUSES = ["Congelado", "Descartado", "Completado", "Perdido"];

/** Alias histórico de `CLOSED_STATUSES`. */
export const BLOCKED_STATUSES = CLOSED_STATUSES;

/** ¿La interacción está fuera del funnel (solo consulta)? */
export const isClosedStatus = (status) => CLOSED_STATUSES.includes(status);

export const isCardDisabled = (element) => isClosedStatus(element?.current_status);

// Cómo se nombra una interacción: manda el nombre del proyecto y, si no lo
// tiene, el del contacto. Misma prioridad que usa `project_display` en las
// tablas de CrmTemplate.
export const interactionTitle = (item) => {
  if (item?.project_name) return item.project_name;
  const contact = `${item?.first_name || ""} ${item?.last_name || ""}`.trim();
  return contact || "Sin nombre";
};

// Días transcurridos desde la fecha de creación de la interacción.
//
// OJO: esto es ANTIGÜEDAD TOTAL, no "días en el estado actual". Ese otro dato
// sale de `status_transitions` y hoy solo existe en el detalle
// (`InteractionManagement/{id}/`), no en los listados del tablero. No mezclar:
// una interacción vieja puede llevar dos días en su estado actual.
export const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const start = new Date(dateStr);
  if (Number.isNaN(start.getTime())) return null;
  // Se compara a medianoche para que "ayer a las 23:00" cuente como 1 día y no
  // como 0 por diferencia de horas.
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - start) / 86400000);
  return diff < 0 ? 0 : diff;
};

/**
 * Índice de tipificación para la barra de progreso: por cada estado, sus etapas
 * aplanadas en orden (fases por `priority`, y dentro de cada una sus etapas por
 * `priority`).
 *
 * ⚠️ El emparejamiento con la tarjeta es POR NOMBRE (fase + etapa), no por id.
 * El listado del tablero (`CrmItxManagementFilter/by-status/`) solo devuelve
 * `phase_name` y `stage_name`; no manda `phase_id` ni `stage_id`, aunque sí
 * `status_id`. PENDIENTE: pedir esos dos ids al backend y cambiar la
 * comparación — falla si un mismo estado llega a tener dos fases con idéntico
 * nombre y ambas una etapa que también coincide.
 *
 * @returns {Map<number, Array<{phase: string, stage: string}>>}
 */
export function buildStageIndex(phases, stages) {
  const index = new Map();
  if (!Array.isArray(phases) || !Array.isArray(stages)) return index;

  const byPriority = (a, b) => (a.priority ?? 0) - (b.priority ?? 0);

  const stagesByPhase = new Map();
  stages.forEach((stage) => {
    const phaseId = stage.crm_phase?.id ?? stage.crm_phase;
    if (phaseId == null) return;
    if (!stagesByPhase.has(phaseId)) stagesByPhase.set(phaseId, []);
    stagesByPhase.get(phaseId).push(stage);
  });

  const phasesByStatus = new Map();
  phases.forEach((phase) => {
    const statusId = phase.crm_status?.id ?? phase.crm_status;
    if (statusId == null) return;
    if (!phasesByStatus.has(statusId)) phasesByStatus.set(statusId, []);
    phasesByStatus.get(statusId).push(phase);
  });

  phasesByStatus.forEach((statusPhases, statusId) => {
    const flat = [];
    [...statusPhases].sort(byPriority).forEach((phase) => {
      const phaseStages = stagesByPhase.get(phase.id) || [];
      [...phaseStages].sort(byPriority).forEach((stage) => {
        flat.push({ phase: phase.name, stage: stage.name });
      });
    });
    index.set(statusId, flat);
  });

  return index;
}

/**
 * Posición de la interacción dentro de las etapas de su estado.
 * `current` es -1 cuando no se pudo emparejar: la barra se pinta completa en
 * gris en vez de inventar una posición.
 *
 * @returns {{current: number, total: number}}
 */
export function stageProgress(element, index) {
  const empty = { current: -1, total: 0 };
  if (!element || !index) return empty;

  const flat = index.get(element.status_id);
  if (!Array.isArray(flat) || !flat.length) return empty;

  const current = flat.findIndex(
    (item) => item.phase === element.phase_name && item.stage === element.stage_name
  );
  return { current, total: flat.length };
}

/**
 * Días que la interacción lleva en su estado ACTUAL.
 *
 * Sale de `status_transitions`, que solo viaja en el detalle
 * (`InteractionManagement/{id}/`); en los listados del tablero no existe.
 *
 * Se toma la entrada MÁS RECIENTE al estado, no la primera: el flujo admite
 * retrocesos, así que un estado puede visitarse más de una vez. Sin ninguna
 * transición se cae a la fecha de creación de la interacción.
 *
 * @returns {number|null}
 */
export function daysInCurrentStatus(interaction) {
  if (!interaction) return null;

  const currentId = interaction.crm_status?.id ?? null;
  const currentName = interaction.crm_status?.name;
  const transitions = Array.isArray(interaction.status_transitions)
    ? interaction.status_transitions
    : [];

  const matches = (transition) => {
    const newId = transition?.new_status?.id;
    if (newId != null && currentId != null) return newId === currentId;
    const newName = transition?.new_status?.name;
    return !!newName && newName === currentName;
  };

  const entries = transitions
    .filter(matches)
    .map((transition) => new Date(transition.created_at).getTime())
    .filter((time) => !Number.isNaN(time));

  const since = entries.length
    ? new Date(Math.max(...entries)).toISOString()
    : interaction.created_at;

  return daysSince(since);
}

export const ageLabel = (dateStr) => {
  const days = daysSince(dateStr);
  if (days === null) return "";
  if (days === 0) return "Hoy";
  return `${days} d`;
};
